<template>
  <div class="gallery-page">
    <h2 class="section-title">Galerie</h2>

    <!-- Team login section -->
    <div v-if="!isAdmin && !isTeamLoggedIn" class="mb-4">
      <div class="team-login-section">
        <div v-if="!showTeamLoginForm" class="text-center">
          <h4>Chcete nahrát nové foto nebo video?</h4>
          <!-- <p class="text-muted mb-3">Přihlaste se pomocí přístupových údajů vašeho týmu.</p> -->
          <button @click="showTeamLoginForm = true" class="btn btn-primary">
            Přihlásit se zde
          </button>
        </div>
        
        <div v-else>
          <div class="text-center mb-3">
            <button @click="showTeamLoginForm = false" class="btn btn-link text-light">
              ← Zpět
            </button>
          </div>
          
          <form @submit.prevent="teamLogin" class="team-login-form">
            <div class="mb-3">
              <label class="form-label">Uživatelské jméno</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="teamLoginForm.username" 
                required 
              />
            </div>
            <div class="mb-3">
              <label class="form-label">Heslo</label>
              <input 
                type="password" 
                class="form-control" 
                v-model="teamLoginForm.password" 
                required 
              />
            </div>
            <button type="submit" class="btn btn-primary" :disabled="teamLoggingIn">
              {{ teamLoggingIn ? 'Přihlašuji...' : 'Přihlásit se' }}
            </button>
            <span v-if="teamLoginError" class="text-danger ms-2">{{ teamLoginError }}</span>
          </form>
        </div>
      </div>
    </div>

    <!-- Upload section (for admins or logged-in teams) -->
    <div v-if="isAdmin || isTeamLoggedIn" class="mb-4">
      <h4>Nahrát nové médium</h4>
      <div v-if="isTeamLoggedIn" class="mb-2">
        <span class="text-success">✓ Přihlášen jako tým</span>
        <button class="btn btn-sm btn-outline-secondary ms-2" @click="teamLogout">Odhlásit se</button>
      </div>
      <form @submit.prevent="uploadMedia" enctype="multipart/form-data">
        <div class="mb-2">
          <input 
            type="file" 
            ref="fileInput" 
            @change="onFileChange" 
            accept="image/*,video/*" 
            multiple 
            required 
          />
          <small class="text-muted d-block mt-1">
            Povolené formáty: obrázky (JPEG, PNG, GIF) a videa (MP4, MOV, AVI). Maximální velikost: 100MB.
          </small>
        </div>
        <button class="btn btn-primary" :disabled="uploading">Nahrát</button>
        <span v-if="uploading" class="ms-2">Nahrávám... ({{ uploadProgress }})</span>
        <span v-if="uploadError" class="text-danger ms-2">{{ uploadError }}</span>
      </form>
    </div>

    <div class="row g-4">
      <div v-for="item in photos" :key="item.id" class="col-12 col-sm-6 col-md-4 col-lg-3">
        <div class="card h-100 gallery-card">
          <!-- Obrázek nebo video podle typu média -->
          <div class="media-container" @click="openLightbox(item)">
            <img 
              v-if="item.media_type === 'photo'" 
              :src="apiUrl + item.url" 
              class="card-img-top" 
              :alt="'Fotka'" 
            />
            <video 
              v-else 
              class="card-img-top" 
              :src="apiUrl + item.url"
              preload="metadata"
            >
              Váš prohlížeč nepodporuje přehrávání videí.
            </video>
            <!-- Indikátor typu média -->
            <div class="media-type-indicator" v-if="item.media_type === 'video'">
              <i class="fas fa-play"></i>
            </div>
          </div>
          <div class="card-body">
            <p class="card-date" v-if="item.uploaded_at">Nahráno: {{ formatDate(item.uploaded_at) }}</p>
            <button v-if="isAdmin" class="btn btn-danger btn-sm mt-2" @click="deletePhoto(item.id)">Smazat</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Lightbox modal -->
    <div v-if="lightboxItem" class="lightbox-overlay" @click.self="closeLightbox">
      <div class="lightbox-content">
        <button class="lightbox-close" @click="closeLightbox">&times;</button>
        <!-- Obrázek nebo video podle typu média -->
        <img 
          v-if="lightboxItem.media_type === 'photo'" 
          :src="apiUrl + lightboxItem.url" 
          :alt="'Fotka'" 
        />
        <video 
          v-else 
          :src="apiUrl + lightboxItem.url"
          controls
          autoplay
          class="lightbox-video"
        >
          Váš prohlížeč nepodporuje přehrávání videí.
        </video>
        <div class="lightbox-caption">
          <p v-if="lightboxItem.uploaded_at" class="card-date">Nahráno: {{ formatDate(lightboxItem.uploaded_at) }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
export default {
  name: 'GalleryView',
  data() {
    return {
      photos: [],
      fileList: [],
      uploading: false,
      uploadError: '',
      isAdmin: false,
      isTeamLoggedIn: false,
      showTeamLoginForm: false,
      teamLoginForm: {
        username: '',
        password: ''
      },
      teamLoggingIn: false,
      teamLoginError: '',
      apiUrl: process.env.VUE_APP_API_URL || '',
      lightboxItem: null,
      uploadProgress: ''
    };
  },
  methods: {
    async fetchPhotos() {
      try {
        const res = await axios.get('/api/gallery');
        this.photos = res.data;
      } catch (err) {
        console.error('Chyba při načítání galerie:', err);
      }
    },
    onFileChange(e) {
      this.fileList = Array.from(e.target.files);
      // Validace velikosti souborů
      const maxSize = 100 * 1024 * 1024; // 100MB
      const invalidFiles = this.fileList.filter(file => file.size > maxSize);
      if (invalidFiles.length > 0) {
        this.uploadError = 'Některé soubory přesahují maximální velikost 100MB.';
        this.fileList = [];
        this.$refs.fileInput.value = '';
      }
    },
    async uploadMedia() {
      if (!this.fileList.length) return;
      this.uploading = true;
      this.uploadError = '';
      this.uploadProgress = '';
      let uploaded = 0;
      
      // Determine which endpoint and token to use
      debugger;
      const endpoint = this.isAdmin ? '/api/admin/gallery' : '/api/team/gallery';
      const token = this.isAdmin ? localStorage.getItem('token') : localStorage.getItem('teamToken');
      
      for (let i = 0; i < this.fileList.length; i++) {
        const file = this.fileList[i];
        const formData = new FormData();
        formData.append('media', file);
        
        try {
          await axios.post(endpoint, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`
            }
          });
          uploaded++;
          this.uploadProgress = `${uploaded} / ${this.fileList.length}`;
        } catch (err) {
          this.uploadError = err.response?.data?.error || 'Chyba při nahrávání.';
          break;
        }
      }
      
      this.fileList = [];
      this.$refs.fileInput.value = '';
      this.uploading = false;
      this.uploadProgress = '';
      this.fetchPhotos();
    },
    async deletePhoto(id) {
      if (!confirm('Opravdu chcete smazat toto médium?')) return;
      try {
        await axios.delete(`/api/admin/gallery/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        this.fetchPhotos();
      } catch (err) {
        alert('Chyba při mazání média.');
      }
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('cs-CZ');
    },
    openLightbox(item) {
      this.lightboxItem = item;
    },
    closeLightbox() {
      if (this.lightboxItem?.media_type === 'video') {
        // Zastav přehrávání videa při zavření lightboxu
        const video = document.querySelector('.lightbox-video');
        if (video) {
          video.pause();
          video.currentTime = 0;
        }
      }
      this.lightboxItem = null;
    },
    async teamLogin() {
      this.teamLoggingIn = true;
      this.teamLoginError = '';
      
      try {
        const response = await axios.post('/api/auth/login', this.teamLoginForm);
        localStorage.setItem('teamToken', response.data.token);
        this.isTeamLoggedIn = true;
        this.teamLoginForm = { username: '', password: '' };
      } catch (error) {
        this.teamLoginError = error.response?.data?.error || 'Chyba při přihlašování';
      } finally {
        this.teamLoggingIn = false;
      }
    },
    teamLogout() {
      localStorage.removeItem('teamToken');
      this.isTeamLoggedIn = false;
    },
    checkTeamLogin() {
      const teamToken = localStorage.getItem('teamToken');
      if (teamToken) {
        this.isTeamLoggedIn = true;
      }
    }
  },
  mounted() {
    this.fetchPhotos();
    if (localStorage.getItem('token')) {
      this.isAdmin = true;
    }
    this.checkTeamLogin();
  }
};
</script>

<style scoped>
.gallery-page {
  max-width: 1200px;
  margin: 0 auto;
  background: #0a1023;
  color: #e0e6f7;
  min-height: 100vh;
  padding: 2rem 1rem 3rem;
}

.section-title {
  background: linear-gradient(135deg, #1fe0ff, #0a7cff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
}

.gallery-card {
  background: #151d3a;
  color: #e0e6f7;
  border: 1px solid rgba(31, 224, 255, 0.08);
  border-radius: 0.75rem;
  margin-bottom: 2rem;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.gallery-card:hover {
  transform: translateY(-4px);
  border-color: rgba(31, 224, 255, 0.2);
  box-shadow: 0 8px 24px rgba(10, 16, 35, 0.4),
              0 0 0 1px rgba(31, 224, 255, 0.05);
}

.card-img-top {
  height: 220px;
  object-fit: cover;
  border-top-left-radius: 0.75rem;
  border-top-right-radius: 0.75rem;
}

.card-title {
  color: #e0e6f7;
  font-weight: 700;
  font-size: 1.1rem;
}

.card-date {
  color: #8892b0;
  font-size: 0.9rem;
}

.btn-danger {
  background: rgba(255, 77, 106, 0.12);
  color: #ff4d6a;
  border: 1px solid rgba(255, 77, 106, 0.25);
  border-radius: 0.5rem;
  font-size: 0.95rem;
  padding: 0.3rem 1.1rem;
  font-weight: 600;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-danger:hover {
  background: #ff4d6a;
  color: #0a1023;
  border-color: #ff4d6a;
}

.btn-primary {
  background: linear-gradient(135deg, #1fe0ff 0%, #0a7cff 100%);
  color: #0a1023;
  font-weight: 700;
  border: none;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(31, 224, 255, 0.2);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(31, 224, 255, 0.35);
  color: #0a1023;
}

.lightbox-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(10, 16, 35, 0.95);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lightbox-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: #151d3a;
  border-radius: 1rem;
  border: 1px solid rgba(31, 224, 255, 0.1);
  box-shadow: 0 16px 48px rgba(10, 16, 35, 0.7);
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.lightbox-content img {
  max-width: 80vw;
  max-height: 70vh;
  border-radius: 0.75rem;
  box-shadow: 0 0 24px rgba(31, 224, 255, 0.15);
}

.lightbox-caption {
  margin-top: 1rem;
  color: #e0e6f7;
  text-align: center;
}

.lightbox-close {
  position: absolute;
  top: 0.5rem;
  right: 1.2rem;
  background: none;
  border: none;
  color: #1fe0ff;
  font-size: 2.2rem;
  cursor: pointer;
  z-index: 10;
  transition: all 0.25s ease;
}

.lightbox-close:hover {
  color: #fff;
  transform: scale(1.1);
}

.media-container {
  position: relative;
  cursor: pointer;
  height: 220px;
  overflow: hidden;
}

.media-container img,
.media-container video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-top-left-radius: 0.75rem;
  border-top-right-radius: 0.75rem;
  transition: transform 0.25s ease;
}

.gallery-card:hover .media-container img,
.gallery-card:hover .media-container video {
  transform: scale(1.03);
}

.media-type-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(10, 16, 35, 0.75);
  color: #1fe0ff;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  border: 1px solid rgba(31, 224, 255, 0.2);
}

.lightbox-video {
  max-width: 80vw;
  max-height: 70vh;
  border-radius: 0.75rem;
  box-shadow: 0 0 24px rgba(31, 224, 255, 0.15);
}

.team-login-section {
  background: #151d3a;
  border-radius: 0.75rem;
  padding: 1rem;
  border: 1px solid rgba(31, 224, 255, 0.1);
}

.team-login-form {
  max-width: 400px;
}

.form-control {
  background: #0a1023;
  border: 1px solid rgba(31, 224, 255, 0.13);
  color: #e0e6f7;
  border-radius: 0.5rem;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.form-control:focus {
  background: #0a1023;
  border-color: #1fe0ff;
  color: #e0e6f7;
  box-shadow: 0 0 0 3px rgba(31, 224, 255, 0.15);
}

.form-label {
  color: #8892b0;
  font-weight: 500;
  font-size: 0.9rem;
}
</style> 